import axios, { CancelToken } from 'axios';
import store from '../store';
import { USER_TYPES } from '../constants';

/** Setup Axios block **/

/**
 * Sets axios Authorization to Basic + token
 * @param token
 */
export function setAuth(token) {
    axios.defaults.headers.common.Authorization = 'Basic ' + token;
}

/**
 * Setups axios authorization and interceptors for the application
 * @param token
 * @param dispatch
 * @param translate
 */
export function setupAxios(token, dispatch, translate) {
    setAuth(token);
    setInterceptors(dispatch, translate);
}

/**
 * Set request and response interceptors for axois. Store and router instances are required for handlers
 * @param dispatch
 * @param translate
 */
export function setInterceptors(dispatch, translate) {
    axios.interceptors.request.use(requestInterceptor);
    axios.interceptors.response.use(
        responseInterceptor(dispatch, translate),
        responseErrorInterceptor(dispatch, translate)
    );
}

/**
 * Remove request and response interceptors for axois.
 */
export function removeInterceptors() {
    axios.interceptors.request.forEach(intersptor => {
        let id = axios.interceptors.request.handlers.indexOf(intersptor);
        axios.interceptors.request.eject(id);
    });
    axios.interceptors.response.forEach(intersptor => {
        let id = axios.interceptors.response.handlers.indexOf(intersptor);
        axios.interceptors.response.eject(id);
    });
}

/**
 * Intercepts requests before it is handled
 * @param request
 * @returns {*}
 */
function requestInterceptor(request) {
    if (request.method.toLowerCase() === 'get' && /(MSIE|Edge|Trident)/.test(navigator.userAgent)) {
        if (!request.params) {
            request.params = {
                _timestamp: Date.now(),
            };
        } else {
            request.params._timestamp = Date.now();
        }
    }
    return request;
}

/**
 * Function is used to handle responses
 * @param dispatch
 * @param translate
 * @returns {function(*=)}
 */
function responseInterceptor(dispatch, translate) {
    return response => {
        let oldTag = store.state.user.xMariTag;
        let newTag = response && response.headers && response.headers['x-mari-tag'];
        if (newTag && newTag !== oldTag) {
            store.commit('user/SET_MARI_TAG', newTag);
            if (oldTag) {
                // Here if oldTag changed to newTag (both were not null/undefined).
                // This means the MARi application source has changed on server afrer MARi install.

                // We need to log user out and refresh the page (he will not be able to save any unsaved changes).
                // Because after page refresh, user will not see messages why he was logged out/refreshed,
                // we first warn him, and then log out and refresh.

                store
                    .dispatch(
                        'confirm/confirmation',
                        {
                            confirmTitle: translate('common.mariUpgradedConfirmTitle'),
                            confirmBody: translate('common.mariUpgradedConfirmBody'),
                            confirmOk: translate('common.ok'),
                            confirmCancel: null,
                        },
                        { root: true }
                    )
                    // No matter what user chose, Refresh or Cancel, he will be logged out and page will refresh
                    .then(() => store.dispatch('user/logout', null, { root: true }))
                    .then(() => location.reload());
            }
        }
        return response;
    };
}

/**
 * Function is used to handle responses with errors
 * @param dispatch
 * @param translate
 * @returns {function(*=)}
 */
function responseErrorInterceptor(dispatch, translate) {
    return error => {
        if (!axios.isCancel(error) && error !== 'canceled') {
            if (
                error &&
                error.request &&
                error.request.status === 0 &&
                error.config &&
                error.config.url !== '/api/v1/wirelessmodules/auto/' &&
                error.config.url !== '/api/v1/cardid/'
            ) {
                dispatch(
                    'popup/popup',
                    {
                        text: translate('common.connectionLost'),
                        type: translate('common.error'),
                    },
                    { root: true }
                );
            }
            if (error && error.request && error.request.status !== 200) {
                if (error.request.status === 401) {
                    if (store.state.user.authorized) {
                        let userType = store.state.user.user.type;
                        store.dispatch('user/logout', null, { root: true }).then(() => {
                            store.commit('user/SET_ERROR', translate('error.user.notAuthorized'));
                            if (userType === USER_TYPES.SUPER_USER) {
                                store.commit('user/SET_NOTIFICATION', {
                                    title: translate('error.user.notAuthorized'),
                                    message: translate('error.user.notAuthorizedSuperUser'),
                                });
                            }
                        });
                    }
                } else {
                    console.log(
                        'Backend error: ' +
                            error.request.status +
                            ' ' +
                            error.request.statusText +
                            '\n' +
                            error.request.responseText +
                            '\n' +
                            error
                    );
                }
            }
        }

        return Promise.reject(
            axios.isCancel(error) || error === 'canceled' ? 'canceled' : formErrorResponse(error)
        );
    };
}

/**
 * Returns:
 * *error.request if the request was made but no response was received
 * *error.response if the request was made and the server responded with a status code
 * *error.response with modified error message if server responded with empty data
 * @param error
 * @returns {*}
 */
function formErrorResponse(error) {
    if (!error.response) {
        return error.request;
    } else if (error.response.data && !(error.response.data instanceof Blob)) {
        return error.response;
    }

    error.response.data = formErrorMessage(error.response);

    return error.response;
}

/**
 * Forms error message with translation key if response.data is empty
 * to display error pop-ups correctly
 * @param response
 * @returns {{error: string}}
 */
function formErrorMessage(response) {
    let errorMessage = '';
    switch (response.status) {
        case 400:
            errorMessage = 'error.common.badRequest';
            break;
        case 401:
            errorMessage = 'error.common.unauthorized';
            break;
        case 403:
            errorMessage = 'error.common.forbidden';
            break;
        case 404:
            errorMessage = 'error.common.notFound';
            break;
        case 409:
            errorMessage = 'error.common.conflict';
            break;
        case 500:
            errorMessage = 'error.common.internalError';
            break;
        case 501:
            errorMessage = 'error.common.notImplemented';
            break;
        default:
            errorMessage = 'common.error';
    }

    return { error: errorMessage };
}

/** Set up resources block **/

/**
 * Creates and returns cancel token with cancel function for it.
 * @returns {{token: CancelToken, cancel: *}}
 */
export const getCancelToken = () => {
    let cancel;
    let token = new CancelToken(c => {
        // An executor function receives a cancel function as a parameter
        cancel = c;
    });
    return { token, cancel };
};

/**
 * Creates the function that constructs URL for Axios API request with passed id
 * @param baseUrl
 * @param suffix
 * @returns {function(*): string}
 */
export const createUrl = (baseUrl, suffix = undefined) => params =>
    baseUrl + (params.id ? params.id + '/' : '') + (suffix ? suffix : '');

/**
 * Creates function to send axios api request with preset URL and passed params {method, id, cancelToken }
 * @param sourceUrl
 * @param method
 * @returns {function(*=)}
 */
export const createResource = (sourceUrl, method = 'get') => (params = {}) => {
    let url = typeof sourceUrl === 'function' ? sourceUrl(params) : sourceUrl;

    return axios({
        url,
        method,
        ...params,
        cancelToken: params.cancelToken,
    });
};

export const createResourceWithUrl = (baseUrl, suffix = undefined) =>
    createResource(createUrl(baseUrl, suffix));
export const createPostResourceWithUrl = (baseUrl, suffix = undefined) =>
    createResource(createUrl(baseUrl, suffix), 'post');
